import React from "react";
import ChrisImage from "../../../../../../assets/images/team/chris.jpg";
import SectionHead from "./SectionHead";

const NoteBlock = ({
  title,
  children,
  accent,
  full,
}: {
  title: string;
  children: React.ReactNode;
  accent?: boolean;
  full?: boolean;
}) => (
  <article
    className={`note-block${accent ? " accent" : ""}${full ? " full" : ""}`}
  >
    <h4>{title}</h4>
    <div className="note-copy">{children}</div>
  </article>
);

const FounderNote = () => (
  <section className="origin" aria-labelledby="origin-title">
    <div className="about-pattern quiet" aria-hidden="true">
      <span className="blob blob-coral"></span>
      <span className="blob blob-lavender"></span>
      <span className="blob blob-yellow"></span>
    </div>
    <div className="wrap">
      <SectionHead
        number="03"
        title="Why we"
        emphasis="started this."
        id="origin-title"
      />
      <div className="note-intro">
        <figure className="founder-polaroid">
          <img src={ChrisImage} alt="Chris Rawlinson, founder of 42courses." />
          <figcaption>
            <span className="fp-name">Chris Rawlinson</span>
            <span className="fp-role">Founder of 42courses</span>
          </figcaption>
        </figure>
        <div className="note-intro-text">
          <span className="note-tag">A note from the founder</span>
          <h3>
            I love learning, but less so the way it's{" "}
            <em>traditionally taught</em>.
          </h3>
        </div>
      </div>
      <div className="note-grid">
        <div className="note-block">
          <h4>Not bad at learning. Bad at how it's traditionally taught.</h4>
          <p>
            I'm dyslexic. I loved school, and I loved a lot of my teachers. The
            way most lessons were delivered, less so. For years I assumed I must
            be bad at learning. Turns out I was bad at{" "}
            <em>that particular way</em> of being taught.
          </p>
        </div>

        <div className="note-block accent">
          <div className="pull">Then in 2006, this happened.</div>
          <p>
            I watched{" "}
            <a
              href="https://www.ted.com/talks/sir_ken_robinson_do_schools_kill_creativity"
              className="inline-link"
              target="_blank"
              rel="noopener"
            >
              Sir Ken Robinson's TED talk
            </a>{" "}
            arguing that schools accidentally kill creativity, and that we
            needed to <strong>fundamentally revolutionise education</strong>. I
            sat there thinking: <em>this is something I'm going to do.</em>
          </p>
          <p
            style={{
              marginTop: "0.6em",
              fontSize: "14px",
              opacity: "0.85",
              fontStyle: "italic",
            }}
          >
            (I subsequently made several dates watch it with me, late, in the
            evening. It did not, in retrospect, help my love life.)
          </p>
        </div>

        <div className="note-block full">
          <h4>So we built the kind of learning I'd want to finish.</h4>
          <p>
            Online learning has a curious problem. Universities optimise their
            platforms to flatter their professors. Tech companies optimise
            theirs to maximise the number of people <em>starting</em> a course.{" "}
            <strong>
              Almost nobody is optimised for the much harder task of getting you
              to enjoy and finish one.
            </strong>{" "}
            Most of us end up with a graveyard of half-watched videos in our
            browser history.
          </p>
          <p>
            We did it differently. 42courses is designed using{" "}
            <strong>behavioural science and learning psychology</strong>,
            gamification, and lessons you can listen to as well as read. We were
            the first elearning platform to use AI to{" "}
            <strong>
              personalise every lesson to your role, location and goals
            </strong>
            . We refine it every week.
          </p>
        </div>

        <div className="note-block full">
          <h4>What you actually get.</h4>
          <p>
            After years at Ogilvy and WPP, I'd come to know many of the
            practitioners I most wanted to learn from.{" "}
            <strong>
              Rory Sutherland, Cindy Gallop, Dan Ariely, Sir John Hegarty, Josy
              Paul, Meik Wiking
            </strong>
            , and dozens of others. <em>Your</em> unfair advantage is that they
            teach you directly. You skip 30 years of trial-and-error and learn
            from the people who already made the mistakes for you.
          </p>
          <p>
            You'll learn the <strong>transferable skills</strong>: storytelling,
            persuasion, strategy, creativity, leadership, asking better
            questions. The tools change every Tuesday; these don't. There's also
            a healthy amount of wellbeing on offer: the <em>Happy Course</em>{" "}
            was so popular we made <em>The Even Happier Course</em>.
          </p>
        </div>

        <div className="note-block full">
          <h4>Oh, and the name.</h4>
          <p>
            <strong>
              42 is the Answer to the Ultimate Question of Life, the Universe,
              and Everything
            </strong>
            , courtesy of Douglas Adams. We picked it in a Cape Town bar in 2014
            after discovering every sensible domain name we liked was taken.
            <span className="footnote">
              Our original tagline was "Learn enough to be dangerous." Retired,
              reluctantly, after several non-English-speaking visitors asked if
              we were teaching bomb-making. We miss it.
            </span>
          </p>
          <div className="note-sig">
            <div className="sig-name">Chris Rawlinson</div>
            <div className="sig-role">Founder &amp; CEO, 42courses</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default FounderNote;
